import { formatDate, formatPhone, formatUZS, normalizePhone, type Sale } from "@elektr/shared";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";

import { ExportButton } from "@/components/ExportButton";
import { Button } from "@/components/ui/button";
import { Card, EmptyState, PageHeader, Spinner } from "@/components/ui/card";
import { ConfirmDialog, Dialog } from "@/components/ui/dialog";
import { Field, Input, Select, Textarea } from "@/components/ui/form";
import { Pagination } from "@/components/ui/pagination";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";

const PAGE_SIZE = 20;
const today = () => new Date().toISOString().slice(0, 10);

/** Sotuvlar: elektrik qo'lda kiritadi (onlayn to'lov yo'q); admin hammasini ko'radi. */
export function SalesPage() {
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const leadId = sp.get("lead");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Sale | "new" | null>(leadId ? "new" : null);
  const [del, setDel] = useState<Sale | null>(null);

  const list = useQuery({
    queryKey: ["sales", { from, to, page }],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/sales/", {
          params: {
            query: {
              date_from: from || undefined,
              date_to: to || undefined,
              page,
              page_size: PAGE_SIZE,
            },
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const remove = useMutation({
    mutationFn: async (s: Sale) => {
      const res = await api.DELETE("/api/sales/{id}/", { params: { path: { id: s.id } } });
      if (!res.response.ok) throw new Error("delete");
    },
    onSuccess: () => {
      setDel(null);
      void qc.invalidateQueries({ queryKey: ["sales"] });
    },
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });
  const rows = list.data?.results ?? [];
  const pageTotal = rows.reduce((sum, s) => sum + (s.total_uzs ?? 0), 0);

  return (
    <>
      <PageHeader
        title={t("nav.sales")}
        actions={
          <>
            <ExportButton
              kind="sales"
              params={{ ...(from ? { date_from: from } : {}), ...(to ? { date_to: to } : {}) }}
            />
            <Button onClick={() => setEditing("new")}>
              <Plus className="size-4" /> {t("sales.new")}
            </Button>
          </>
        }
      />
      <Card className="mb-4 grid gap-3 sm:grid-cols-2">
        <Field label={t("sales.from")}>
          {(id) => (
            <Input
              id={id}
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(1);
              }}
            />
          )}
        </Field>
        <Field label={t("sales.to")}>
          {(id) => (
            <Input
              id={id}
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(1);
              }}
            />
          )}
        </Field>
      </Card>

      {list.isLoading && <Spinner className="m-8 size-8" />}
      {list.data && rows.length === 0 && <EmptyState>{t("sales.empty")}</EmptyState>}
      {rows.length > 0 && (
        <>
          <Card className="divide-y divide-border p-0">
            {rows.map((s) => (
              <div
                key={s.id}
                className="flex flex-wrap items-start justify-between gap-3 p-4"
                data-testid="sale-row"
              >
                <div className="min-w-0">
                  <div className="font-semibold">{formatUZS(s.total_uzs ?? 0)}</div>
                  <div className="text-sm text-muted-foreground">
                    {formatDate(s.sold_on)} · {s.customer_name || "—"}
                    {s.customer_phone ? ` · ${formatPhone(s.customer_phone)}` : ""}
                    {s.electrician_name ? ` · ${s.electrician_name}` : ""}
                  </div>
                  <div className="text-sm">
                    {s.items.map((i) => `${i.name} × ${i.quantity}`).join(", ")}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={t("common.edit")}
                    onClick={() => setEditing(s)}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={t("common.delete")}
                    onClick={() => setDel(s)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>
            ))}
          </Card>
          <p className="mt-2 text-right text-sm text-muted-foreground" data-testid="page-total">
            {t("sales.page_total")}:{" "}
            <strong className="text-foreground">{formatUZS(pageTotal)}</strong>
          </p>
        </>
      )}
      <Pagination page={page} count={list.data?.count ?? 0} pageSize={PAGE_SIZE} onPage={setPage} />

      {editing && (
        <SaleDialog
          sale={editing === "new" ? undefined : editing}
          leadId={editing === "new" ? leadId : null}
          onClose={() => {
            setEditing(null);
            if (leadId) setSp({}, { replace: true });
          }}
        />
      )}
      <ConfirmDialog
        open={!!del}
        onOpenChange={(o) => !o && setDel(null)}
        title={t("sales.delete_title")}
        message={t("sales.delete_message")}
        confirmLabel={t("common.delete")}
        pending={remove.isPending}
        onConfirm={() => del && remove.mutate(del)}
      />
    </>
  );
}

interface ItemRow {
  product: string;
  name: string;
  quantity: string;
  price: string;
}

function SaleDialog({
  sale,
  leadId,
  onClose,
}: {
  sale?: Sale;
  leadId: string | null;
  onClose: () => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [customerName, setCustomerName] = useState(sale?.customer_name ?? "");
  const [customerPhone, setCustomerPhone] = useState(sale?.customer_phone ?? "");
  const [soldOn, setSoldOn] = useState(sale?.sold_on ?? today());
  const [note, setNote] = useState(sale?.note ?? "");
  const [items, setItems] = useState<ItemRow[]>(
    sale
      ? sale.items.map((i) => ({
          product: i.product ?? "",
          name: i.name ?? "",
          quantity: String(i.quantity ?? 1),
          price: String(i.unit_price_uzs ?? ""),
        }))
      : [{ product: "", name: "", quantity: "1", price: "" }],
  );
  const [error, setError] = useState<string | null>(null);

  const products = useQuery({
    queryKey: ["sale-products"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/products/", {
          params: { query: { page_size: 100, ordering: "name" } },
        }),
      ),
  });
  // So'rovdan sotuv kiritilsa, mijoz ma'lumoti avtomatik to'ldiriladi
  useQuery({
    queryKey: ["lead-prefill", leadId],
    enabled: !!leadId && !sale,
    queryFn: async () => {
      const l = unwrap(await api.GET("/api/leads/{id}/", { params: { path: { id: leadId! } } }));
      setCustomerName(l.name ?? "");
      setCustomerPhone(l.phone);
      if (l.product) setItems([{ product: l.product, name: "", quantity: "1", price: "" }]);
      return l;
    },
  });

  const pick = (i: number, productId: string) => {
    const p = products.data?.results.find((x) => x.id === productId);
    setItems((rows) =>
      rows.map((r, j) =>
        j === i
          ? {
              ...r,
              product: productId,
              name: p?.name ?? r.name,
              price: p ? String(p.price_uzs) : r.price,
            }
          : r,
      ),
    );
  };
  const total = items.reduce(
    (sum, r) => sum + (Number(r.quantity) || 0) * (Number(r.price) || 0),
    0,
  );

  const save = useMutation({
    mutationFn: async () => {
      let phone = "";
      if (customerPhone.trim()) {
        const n = normalizePhone(customerPhone);
        if (!n) throw new Error("phone");
        phone = n;
      }
      const body = {
        customer_name: customerName,
        customer_phone: phone,
        sold_on: soldOn,
        note,
        ...(leadId && !sale ? { lead: leadId } : {}),
        items: items.map((r) => ({
          ...(r.product ? { product: r.product } : {}),
          name: r.name,
          quantity: Number(r.quantity),
          unit_price_uzs: Number(r.price),
        })),
      };
      return sale
        ? unwrap(await api.PUT("/api/sales/{id}/", { params: { path: { id: sale.id } }, body }))
        : unwrap(await api.POST("/api/sales/", { body }));
    },
    onSuccess: () => {
      toast.success(t("common.saved"));
      void qc.invalidateQueries({ queryKey: ["sales"] });
      void qc.invalidateQueries({ queryKey: ["leads"] });
      onClose();
    },
    onError: (e) =>
      setError(
        e instanceof Error && e.message === "phone"
          ? t("auth.phone_invalid")
          : errorMessage(e, t("common.error")),
      ),
  });

  const valid =
    items.length > 0 &&
    items.every(
      (r) => r.name.trim() && Number(r.quantity) >= 1 && r.price !== "" && Number(r.price) >= 0,
    );

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={sale ? t("sales.edit") : t("sales.new")}
    >
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setError(null);
          save.mutate();
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("sales.customer_name")}>
            {(id) => (
              <Input
                id={id}
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
            )}
          </Field>
          <Field label={t("sales.customer_phone")}>
            {(id) => (
              <Input
                id={id}
                type="tel"
                inputMode="tel"
                placeholder="90 123 45 67"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
              />
            )}
          </Field>
          <Field label={t("sales.date")}>
            {(id) => (
              <Input
                id={id}
                type="date"
                max={today()}
                value={soldOn}
                onChange={(e) => setSoldOn(e.target.value)}
              />
            )}
          </Field>
        </div>

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-medium">{t("sales.items")}</legend>
          {items.map((r, i) => (
            <div
              key={i}
              className="grid grid-cols-[1fr_4.5rem_6.5rem_auto] items-end gap-2 rounded-lg border border-border p-2"
            >
              <div className="col-span-4 grid gap-2 sm:col-span-1">
                <Select
                  aria-label={`${t("sales.product")} ${i + 1}`}
                  value={r.product}
                  onChange={(e) => pick(i, e.target.value)}
                >
                  <option value="">{t("sales.other_item")}</option>
                  {products.data?.results.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
                <Input
                  aria-label={`${t("sales.item_name")} ${i + 1}`}
                  placeholder={t("sales.item_name")}
                  value={r.name}
                  onChange={(e) =>
                    setItems((rows) =>
                      rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)),
                    )
                  }
                />
              </div>
              <Input
                aria-label={`${t("sales.quantity")} ${i + 1}`}
                type="number"
                inputMode="numeric"
                min={1}
                value={r.quantity}
                onChange={(e) =>
                  setItems((rows) =>
                    rows.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)),
                  )
                }
              />
              <Input
                aria-label={`${t("sales.unit_price")} ${i + 1}`}
                type="number"
                inputMode="numeric"
                min={0}
                value={r.price}
                onChange={(e) =>
                  setItems((rows) =>
                    rows.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)),
                  )
                }
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label={`${t("common.delete")} ${i + 1}`}
                disabled={items.length === 1}
                onClick={() => setItems((rows) => rows.filter((_, j) => j !== i))}
              >
                <X className="size-4" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() =>
              setItems((rows) => [...rows, { product: "", name: "", quantity: "1", price: "" }])
            }
          >
            <Plus className="size-4" /> {t("sales.add_item")}
          </Button>
        </fieldset>

        <Field label={t("props.notes")}>
          {(id) => (
            <Textarea
              id={id}
              rows={2}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          )}
        </Field>
        <p className="text-right text-lg font-bold" data-testid="sale-total">
          {t("sales.total")}: {formatUZS(total)}
        </p>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={!valid || save.isPending}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
